import { useEffect, useState } from "react"
import { useParams, useNavigate } from "react-router-dom"

function UserProfile() {
  const { id } = useParams()
  const [user, setUser] = useState(null)
  const navigate = useNavigate()
  useEffect(() => {
    const getUser = async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/users/${id}`,
          { credentials: "include" },
        )

        if (!res.ok) throw new Error("Failed to fetch user")

        const data = await res.json()
        setUser(data)
      } catch (err) {
        console.error(err)
      }
    }

    getUser()
  }, [id])

  if (!user) return <div className="text-center mt-5">Loading...</div>

  /* =========================
     HELPERS
  ========================== */
  const formatAnswer = (value) => {
    if (Array.isArray(value)) return value.join(", ")
    if (typeof value === "boolean") return value ? "Yes" : "No"
    if (value === "yes" || value === "no") return value === "yes" ? "Yes" : "No"
    return value || "—"
  }

  /* =========================
     GROUP ANSWERS (SECTION)
  ========================== */
  const groupedAnswers = {}

  Object.entries(user.onboarding_answers || {}).forEach(([fieldId, item]) => {
    const section = item.section || "General"

    if (!groupedAnswers[section]) {
      groupedAnswers[section] = []
    }

    groupedAnswers[section].push(item)
  })

  return (
    <div className="container py-4">
      {/* =========================
         HERO HEADER
      ========================== */}
      <div className="mb-3">
        <button
          className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-2"
          onClick={() => navigate(-1)}
        >
          ← Back
        </button>
      </div>
      <div className="card shadow-sm mb-4 p-4 border-0">
        <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
          <div className="d-flex gap-3 align-items-center">
            {/* Avatar */}
            <div
              className="rounded-circle bg-dark text-white d-flex align-items-center justify-content-center"
              style={{ width: 70, height: 70, fontSize: 26 }}
            >
              {user.username?.charAt(0).toUpperCase()}
            </div>

            <div>
              <h3 className="mb-1">{user.username}</h3>
              <div className="text-muted">{user.email}</div>

              {/* Status Chips */}
              <div className="mt-2 d-flex gap-2 flex-wrap">
                <span className="badge bg-light text-dark border">
                  {user.account_type}
                </span>

                <span
                  className={`badge ${
                    user.email_verified ? "bg-success" : "bg-secondary"
                  }`}
                >
                  {user.email_verified ? "Email Verified" : "Unverified"}
                </span>

              </div>
            </div>
          </div>

          {/* Metadata */}
          <div className="text-end text-muted small">
            <div>
              <strong>Created:</strong>{" "}
              {new Date(user.createdAt).toLocaleDateString()}
            </div>
            <div>
              <strong>Updated:</strong>{" "}
              {new Date(user.updatedAt).toLocaleDateString()}
            </div>
          </div>
        </div>

        {/* Activity */}
        <div className="row mt-4 text-center">
          <div className="col-md-4">
            <div className="fw-bold">Cart</div>
            <div>{user.cart.length} items</div>
          </div>

          <div className="col-md-4">
            <div className="fw-bold">Lists</div>
            <div>{user.lists.length}</div>
          </div>

          <div className="col-md-4">
            <div className="fw-bold">Addresses</div>
            <div>{user.addresses.length}</div>
          </div>
        </div>
      </div>

      {/* =========================
         ADDRESSES (IMPROVED GRID)
      ========================== */}
      <div className="card shadow-sm mb-4 p-4 border-0">
        <h5 className="mb-3">Saved Addresses</h5>

        {user.addresses.length === 0 ? (
          <div className="text-muted">No addresses saved</div>
        ) : (
          <div className="row g-3">
            {user.addresses.map((addr) => (
              <div key={addr._id} className="col-md-6">
                <div className="border rounded p-3 h-100 bg-light">
                  <div className="fw-bold">{addr.full_name}</div>

                  <div className="small text-muted mt-1">
                    {addr.address1}
                    {addr.address2 && `, ${addr.address2}`}
                  </div>

                  <div className="mt-2">
                    {addr.city}, {addr.state}
                  </div>

                  <div>
                    {addr.country} {addr.zip}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* =========================
         BUSINESSES (from sign-up)
      ========================== */}
      <div className="card shadow-sm p-4 border-0 mb-4">
        <h5 className="mb-3">Businesses</h5>
        {(user.businesses || []).length === 0 ? (
          <div className="text-muted">No businesses added</div>
        ) : (
          <div className="row g-3">
            {user.businesses.map((b, i) => (
              <div key={b._id || i} className="col-md-6">
                <div className="border rounded-3 p-3 h-100">
                  <div className="fw-semibold">{b.name}</div>
                  <div className="text-muted small mb-2">
                    {b.type}
                    {b.subtype ? ` › ${b.subtype}` : ""}
                  </div>
                  {b.email && <div className="small">{b.email}</div>}
                  {b.phone && <div className="small">{b.phone}</div>}
                  <div className="small text-muted">
                    {[b.address?.line1, b.address?.city, b.address?.province, b.address?.zip]
                      .filter(Boolean)
                      .join(", ")}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* =========================
         SIGN-UP ANSWERS (TIMELINE STYLE)
      ========================== */}
      <div className="card shadow-sm p-4 border-0">
        <h5 className="mb-4">Sign-up answers</h5>

        {Object.keys(groupedAnswers).length === 0 ? (
          <div className="text-muted">No answers to extra sign-up questions</div>
        ) : (
          Object.entries(groupedAnswers).map(([section, items]) => (
            <div key={section} className="mb-4">
              <h6 className="text-uppercase text-muted mb-3">{section}</h6>

              <div className="position-relative ps-3">
                {/* Vertical line */}
                <div
                  style={{
                    position: "absolute",
                    left: "8px",
                    top: 0,
                    bottom: 0,
                    width: "2px",
                    background: "#dee2e6",
                  }}
                />

                {items.map((item, idx) => (
                  <div key={idx} className="mb-4 position-relative">
                    {/* Dot */}
                    <div
                      style={{
                        position: "absolute",
                        left: "-2px",
                        top: "6px",
                        width: "12px",
                        height: "12px",
                        background: "#0d6efd",
                        borderRadius: "50%",
                      }}
                    />

                    <div>
                      <div className="fw-semibold">{item.question}</div>

                      <div className="text-muted">
                        {formatAnswer(item.answer)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  )
}

export default UserProfile
